import { Vertex } from '../core/Vertex';

export type Axis = 'x' | 'y' | 'z';

export interface AlignVerticesToAxisOptions {
  /** Explicit target coordinate along the chosen axis. If omitted, a statistic is used. */
  target?: number;
  /** Statistic to derive the target when not provided. Defaults to `mean`. */
  strategy?: 'mean' | 'median';
  /** Optional snapping interval applied to the resulting target. */
  snap?: number;
  /** Threshold below which movements are ignored when counting aligned vertices. */
  epsilon?: number;
}

/**
 * Aligns all provided vertices so that they share the same coordinate along a single axis.
 *
 * The target value can either be supplied explicitly, or derived from the current
 * vertex positions using the requested `strategy` (mean or median). A snapping interval
 * can be provided to quantise the resulting coordinate.
 *
 * @param vertices Collection of vertices to align.
 * @param axis Axis to align along (`'x'`, `'y'`, or `'z'`).
 * @param options Optional alignment preferences.
 * @returns The number of vertices that were moved by more than `epsilon`.
 */
export function alignVerticesToAxis(
  vertices: Iterable<Vertex>,
  axis: Axis,
  options: AlignVerticesToAxisOptions = {},
): number {
  const verts = Array.from(vertices);
  if (verts.length === 0) {
    return 0;
  }

  const { target, strategy = 'mean', snap, epsilon = 0 } = options;

  let targetValue = target;
  if (targetValue === undefined) {
    const values = verts.map((v) => v.position[axis]);
    if (values.length === 0) {
      return 0;
    }

    if (strategy === 'median') {
      const sorted = values.slice().sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      targetValue =
        sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) * 0.5 : sorted[mid];
    } else {
      const sum = values.reduce((acc, value) => acc + value, 0);
      targetValue = sum / values.length;
    }
  }

  if (snap !== undefined && snap > 0) {
    targetValue = Math.round(targetValue / snap) * snap;
  }

  let movedCount = 0;
  for (const vertex of verts) {
    if (Math.abs(vertex.position[axis] - targetValue) > epsilon) {
      movedCount++;
    }
    vertex.position[axis] = targetValue;
  }

  return movedCount;
}
